import { Inject, LOCALE_ID, Pipe, PipeTransform } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { ChargedHolder, CurrencyService } from '../services/currency.service';

/// Writes an amount already in the shown currency: its symbol, its decimals, grouped the en-US way.
function write(pipe: CurrencyPipe, service: CurrencyService, amount: number): string | null {
    const o = service.options;
    if (!service.isLocal) {
        return CurrencyService.writeSite(amount);
    }
    const d = service.decimals;
    return pipe.transform(amount, o.code, o.display || o.code, `1.${d}-${d}`, 'en-US');
}

/// A stored amount as the visitor is shown it: converted at the live rate (CurrencyService.convert). Pass 'stored' for
/// the amount as it is kept - what the owner's and sellers' screens count: written in the site's currency, except for the
/// owner with a currency picked in the top bar, who sees it in that (CurrencyService.stored).
@Pipe({
    name: 'currencyFormat',
    pure: false
})
export class CurrencyFormatPipe implements PipeTransform {
    private readonly currencyPipe: CurrencyPipe;

    constructor(@Inject(LOCALE_ID) locale: string, private service: CurrencyService) {
        this.currencyPipe = new CurrencyPipe(locale);
    }

    transform(value: any, currencyCode?: string): string | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }
        if (currencyCode === 'stored') {
            return this.service.stored(Number(value));
        }
        return write(this.currencyPipe, this.service, this.service.convert(Number(value)));
    }
}

/// An amount quoted in a currency the data names - a supplier's price, a delivery fee - `fee | quotedMoney:row.Currency`:
/// the site's currency (or none named) as a stored amount, so the owner's top-bar pick applies (CurrencyService.quoted);
/// any other currency as it is, never converted.
@Pipe({
    name: 'quotedMoney',
    pure: false
})
export class QuotedMoneyPipe implements PipeTransform {
    constructor(private service: CurrencyService) { }

    transform(value: any, currencyCode?: string): string | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }
        return this.service.quoted(Number(value), currencyCode);
    }
}

/// An amount that is ALREADY in the shown currency - a line or a total from CurrencyService.basket - written, never
/// converted again.
@Pipe({
    name: 'localMoney',
    pure: false
})
export class LocalMoneyPipe implements PipeTransform {
    private readonly currencyPipe: CurrencyPipe;

    constructor(@Inject(LOCALE_ID) locale: string, private service: CurrencyService) {
        this.currencyPipe = new CurrencyPipe(locale);
    }

    transform(value: any): string | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }
        return write(this.currencyPipe, this.service, Number(value));
    }
}

/// The site's own currency as copy names it - `{{ 'code' | siteCurrency }}` is "BDT", `{{ 'name' | siteCurrency }}`
/// "Bangladeshi taka" (CurrencyService.site, SiteMoney on the server). Not pure: it arrives with the first Locale/me answer.
@Pipe({
    name: 'siteCurrency',
    pure: false
})
export class SiteCurrencyPipe implements PipeTransform {
    transform(field: 'code' | 'name' | 'symbol'): string {
        const site = CurrencyService.site;
        return field === 'name' ? site.name : field === 'symbol' ? site.symbol : site.code;
    }
}

/// An amount ALREADY in the currency a past order was charged in (CurrencyService.orderCharge) - `amount | chargedMoney:order`.
@Pipe({
    name: 'chargedMoney'
})
export class ChargedMoneyPipe implements PipeTransform {
    private readonly currencyPipe: CurrencyPipe;

    constructor(@Inject(LOCALE_ID) locale: string) {
        this.currencyPipe = new CurrencyPipe(locale);
    }

    transform(value: any, holder: ChargedHolder): string | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }
        const code = holder && holder.ChargedCurrency ? holder.ChargedCurrency : CurrencyService.site.code;
        if (!code || code === CurrencyService.site.code) {
            return CurrencyService.writeSite(Number(value));
        }
        const d = CurrencyService.chargedAt(holder).decimals;
        return this.currencyPipe.transform(Number(value), code, holder.ChargedSymbol || code, `1.${d}-${d}`, 'en-US');
    }
}

/// A past order's line as it was charged - `unitUsd | chargedLine:order:quantity` - the unit converted once at the
/// order's own rate, times the quantity.
@Pipe({
    name: 'chargedLine'
})
export class ChargedLinePipe implements PipeTransform {
    private readonly money: ChargedMoneyPipe;

    constructor(@Inject(LOCALE_ID) locale: string) {
        this.money = new ChargedMoneyPipe(locale);
    }

    transform(unitUsd: any, holder: ChargedHolder, quantity: number = 1): string | null {
        if (unitUsd === null || unitUsd === undefined || unitUsd === '') {
            return null;
        }
        return this.money.transform(CurrencyService.chargedLine(Number(unitUsd), quantity, holder), holder);
    }
}

/// A basket line - `unitUsd | currencyLine:quantity` - as shown: the unit price converted once, times the quantity.
@Pipe({
    name: 'currencyLine',
    pure: false
})
export class CurrencyLinePipe implements PipeTransform {
    private readonly currencyPipe: CurrencyPipe;

    constructor(@Inject(LOCALE_ID) locale: string, private service: CurrencyService) {
        this.currencyPipe = new CurrencyPipe(locale);
    }

    transform(unitUsd: any, quantity: number): string | null {
        if (unitUsd === null || unitUsd === undefined || unitUsd === '') {
            return null;
        }
        return write(this.currencyPipe, this.service, this.service.lineTotal(Number(unitUsd), quantity));
    }
}
